/**
 * Axios Instance with Auto-Refresh on 401
 * Architecture:
 * - Access token stocké en mémoire (via setAccessToken)
 * - Refresh token géré par tokenStorage
 * - Intercepteur request: ajoute Bearer token
 * - Intercepteur response: tente refresh sur 401 (une seule fois)
 */

import axios from "axios";

const api = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL || "",
  // withCredentials: true, // Désactivé car on utilise Bearer tokens
});

// Access token en mémoire (JAMAIS dans localStorage)
let accessToken = null;

export const setAccessToken = (token) => {
  accessToken = token;
};

export const clearAccessToken = () => {
  accessToken = null;
};

export const getAccessToken = () => accessToken;

// Gestion du refresh en cours (évite les appels multiples simultanés)
let isRefreshing = false;
let refreshSubscribers = [];

const subscribeTokenRefresh = (callback) => {
  refreshSubscribers.push(callback);
};

const onRefreshed = (newToken) => {
  refreshSubscribers.forEach((callback) => callback(newToken));
  refreshSubscribers = [];
};

// Intercepteur REQUEST: ajoute le Bearer token
api.interceptors.request.use(
  (config) => {
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

/**
 * Attache les intercepteurs de refresh automatique
 * Doit être appelé UNE SEULE FOIS depuis le AuthProvider
 */
export const attachAuthInterceptors = ({ refreshAuth, logout }) => {
  api.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      // Routes d'authentification (ne pas tenter de refresh)
      const isAuthRoute =
        originalRequest?.url?.includes("/api/auth/login") ||
        originalRequest?.url?.includes("/api/auth/refresh") ||
        originalRequest?.url?.includes("/api/auth/forgot-password") ||
        originalRequest?.url?.includes("/api/auth/set-password") ||
        originalRequest?.url?.includes("/api/auth/validate-token");

      // Ne pas refresh si:
      // - Pas un 401
      // - Déjà tenté (_retry)
      // - Route d'authentification
      if (error.response?.status !== 401 || originalRequest?._retry || isAuthRoute) {
        return Promise.reject(error);
      }

      // Si un refresh est déjà en cours, attendre le résultat
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          subscribeTokenRefresh((newToken) => {
            if (!newToken) {
              reject(error);
              return;
            }

            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            resolve(api(originalRequest));
          });
        });
      }

      // Marquer comme "déjà tenté" pour éviter boucle infinie
      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Tenter le refresh
        await refreshAuth();
        const newToken = getAccessToken();
        onRefreshed(newToken);

        // Retry la requête originale avec le nouveau token
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        // Refresh échoué → logout
        onRefreshed(null);
        await logout({ callApi: false });
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
  );
};

export default api;
